/**
 * Course outline helpers for titles, navigation, and quiz detection.
 */

import { isPrimedCourse } from "offline/firstLoad";

/**
 * True when the course outline should be loaded from GraphQL.
 * Skips while the first-load bundle is in flight, and skips the course that
 * bundle already applied. Later navigations still use the original API.
 *
 * @param {boolean} sessionHydrated Store hydration flag.
 * @param {string} [courseId] Course id from the route.
 * @param {boolean} [firstLoadPending=false] Combined first-load request in flight.
 * @returns {boolean}
 */
export const shouldFetchCourseDetail = (sessionHydrated, courseId, firstLoadPending = false) => {
  if (!sessionHydrated || !courseId || firstLoadPending) return false;
  if (isPrimedCourse(courseId)) return false;
  return true;
};

/**
 * Picks the next virtual class for a course. Prefers dashboard meetings linked
 * through `meet_course` (same source as Assigned), then attendee upcoming rows.
 *
 * @param {{ data?: Array }} meetEvents Dashboard meeting list.
 * @param {{ data?: Array }} upcomingSessions Course-detail upcoming list.
 * @param {string} courseId Course identifier.
 * @returns {Object}
 */
export const pickCourseVirtualSession = (meetEvents, upcomingSessions, courseId) => {
  if (!courseId) return {};
  const linked = ((meetEvents && meetEvents.data) || []).filter((session) => ((session && session.courses) || []).some((course) => course && course.id === courseId));
  if (linked.length) {
    return [...linked].sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")))[0];
  }
  return ((upcomingSessions && upcomingSessions.data) || [])[0] || {};
};

/**
 * Counts problem XBlocks in a sequence's verticals.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @param {number|string} sequenceIndex Sequence index.
 * @returns {number}
 */
export const getProblemBlockNumber = (structure, sectionIndex, sequenceIndex) => {
  sectionIndex = Number(sectionIndex);
  sequenceIndex = Number(sequenceIndex);

  if (!structure || !Object.keys(structure).length || !Object.prototype.hasOwnProperty.call(structure, "sections")) return 0;
  if (sectionIndex >= structure.sections.length) return 0;
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex], "sequences")) return 0;
  if (sequenceIndex >= structure.sections[sectionIndex].sequences.length) return 0;

  let problemBlockCounter = 0;
  for (let sequence of structure.sections[sectionIndex].sequences) {
    if (!Object.prototype.hasOwnProperty.call(sequence, "verticals") || !Array.isArray(sequence.verticals)) continue;

    for (let vertical of sequence.verticals) {
      if (!Object.prototype.hasOwnProperty.call(vertical, "xblock_ids") || !Array.isArray(vertical.xblock_ids)) continue;

      problemBlockCounter += vertical.xblock_ids.filter((v) => v.type === "problem").length;
    }
  }

  return problemBlockCounter;
};

/**
 * Returns the vertical usage ID at the given outline indexes.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @param {number|string} sequenceIndex Sequence index.
 * @param {number|string} verticalIndex Vertical index.
 * @returns {string}
 */
export const getVerticalIdFromIndex = (structure, sectionIndex, sequenceIndex, verticalIndex) => {
  sectionIndex = Number(sectionIndex);
  sequenceIndex = Number(sequenceIndex);
  verticalIndex = Number(verticalIndex);

  if (!structure || !Object.keys(structure).length || !Object.prototype.hasOwnProperty.call(structure, "sections")) return "";
  if (sectionIndex >= structure.sections.length) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex], "sequences")) return "";
  if (sequenceIndex >= structure.sections[sectionIndex].sequences.length) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex].sequences[sequenceIndex], "verticals")) return "";
  if (verticalIndex >= structure.sections[sectionIndex].sequences[sequenceIndex].verticals.length) return "";

  return structure.sections[sectionIndex].sequences[sequenceIndex].verticals[verticalIndex].vertical_id;
};

/**
 * Extracts a numeric module index from a section title such as "MODULE 3".
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @returns {string|null}
 */
export const getModuleIndexOfSection = (structure, sectionIndex) => {
  const title = getSectionTitle(structure, sectionIndex);
  const matchList = title.toUpperCase().match(/^MODULE (\d)+.*$/);

  if (matchList) {
    return matchList[1];
  }
  return null;
};

/**
 * Returns the section title at the given index.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @returns {string}
 */
export const getSectionTitle = (structure, sectionIndex) => {
  sectionIndex = Number(sectionIndex);

  if (!structure || !Object.keys(structure).length || !Object.prototype.hasOwnProperty.call(structure, "sections")) return "";
  if (sectionIndex >= structure.sections.length || !structure.sections[sectionIndex]) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex], "section_title")) return "";

  return structure.sections[sectionIndex].section_title;
};

/**
 * Returns the sequence title at the given indexes.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @param {number|string} sequenceIndex Sequence index.
 * @returns {string}
 */
export const getSequenceTitle = (structure, sectionIndex, sequenceIndex) => {
  sectionIndex = Number(sectionIndex);
  sequenceIndex = Number(sequenceIndex);

  if (!structure || !Object.keys(structure).length || !Object.prototype.hasOwnProperty.call(structure, "sections")) return "";
  if (sectionIndex >= structure.sections.length || !structure.sections[sectionIndex]) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex], "sequences")) return "";
  if (sequenceIndex >= structure.sections[sectionIndex].sequences.length || !structure.sections[sectionIndex].sequences[sequenceIndex]) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex].sequences[sequenceIndex], "verticals")) return "";

  return structure.sections[sectionIndex].sequences[sequenceIndex].sequence_title;
};

/**
 * Returns the vertical title at the given indexes.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @param {number|string} sequenceIndex Sequence index.
 * @param {number|string} verticalIndex Vertical index.
 * @returns {string}
 */
export const getVerticalTitle = (structure, sectionIndex, sequenceIndex, verticalIndex) => {
  sectionIndex = Number(sectionIndex);
  sequenceIndex = Number(sequenceIndex);
  verticalIndex = Number(verticalIndex);

  if (!structure || !Object.keys(structure).length || !Object.prototype.hasOwnProperty.call(structure, "sections")) return "";
  if (sectionIndex >= structure.sections.length || !structure.sections[sectionIndex]) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex], "sequences")) return "";
  if (sequenceIndex >= structure.sections[sectionIndex].sequences.length || !structure.sections[sectionIndex].sequences[sequenceIndex]) return "";
  if (!Object.prototype.hasOwnProperty.call(structure.sections[sectionIndex].sequences[sequenceIndex], "verticals")) return "";
  if (verticalIndex >= structure.sections[sectionIndex].sequences[sequenceIndex].verticals.length) return "";

  return structure.sections[sectionIndex].sequences[sequenceIndex].verticals[verticalIndex].vertical_title;
};

/**
 * Returns the first playable vertical route (`i/j/k`) or null when the outline is empty.
 *
 * @param {Object} structure Course outline.
 * @returns {string|null}
 */
export const getFirstVerticalRoute = (structure) => {
  const sections = (structure && structure.sections) || [];
  for (let i = 0; i < sections.length; i++) {
    const sequences = (sections[i] && sections[i].sequences) || [];
    for (let j = 0; j < sequences.length; j++) {
      const verticals = (sequences[j] && sequences[j].verticals) || [];
      if (verticals.length) return `${i}/${j}/0`;
    }
  }
  return null;
};

/**
 * True when the outline indexes point at an existing vertical.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @param {number|string} sequenceIndex Sequence index.
 * @param {number|string} verticalIndex Vertical index.
 * @returns {boolean}
 */
export const hasVerticalAt = (structure, sectionIndex, sequenceIndex, verticalIndex) =>
  Boolean(getVerticalIdFromIndex(structure, sectionIndex, sequenceIndex, verticalIndex));

/**
 * Builds previous and next vertical routes relative to the current unit.
 *
 * @param {Object} structure Course outline.
 * @param {number|string} sectionIndex Section index.
 * @param {number|string} sequenceIndex Sequence index.
 * @param {number|string} verticalIndex Vertical index.
 * @returns {{ prev: Object, next: Object }}
 */
export const getPrevNextLinks = (structure, sectionIndex, sequenceIndex, verticalIndex) => {
  // Set Next/Prev link
  let arr = [];

  if (!structure || !Array.isArray(structure.sections)) return {};
  for (let i = 0; i < structure.sections.length; i++) {
    const section = structure.sections[i];
    if (!section.sequences) continue;
    for (let j = 0; j < section.sequences.length; j++) {
      const sequence = section.sequences[j];
      if (!sequence.verticals) continue;
      for (let k = 0; k < sequence.verticals.length; k++) {
        arr.push({
          route: [i, j, k],
          title: sequence.verticals[k]["vertical_title"],
        });
      }
    }
  }

  const currLink = JSON.stringify([Number(sectionIndex), Number(sequenceIndex), Number(verticalIndex)]);
  for (let i = 0; i < arr.length; i++) {
    if (JSON.stringify(arr[i]["route"]) === currLink) {
      const prev = {
        route: i > 0 ? arr[i - 1]["route"].join("/") : null,
        title: i > 0 ? arr[i - 1]["title"] : null,
      };
      const next = {
        route: i < arr.length - 1 ? arr[i + 1]["route"].join("/") : null,
        title: i < arr.length - 1 ? arr[i + 1]["title"] : null,
      };

      return { prev: prev, next: next };
    }
  }

  return { prev: {}, next: {} };
};

/**
 * Maps each section to the first vertical route that can open it.
 *
 * @param {Object} structure Course outline.
 * @returns {Array<{ route: string, title: string }>}
 */
export const getCandidateLinks = (structure) => {
  // Set Next/Prev link
  let arr = [];
  for (let i = 0; structure.sections && i < structure.sections.length; i++) {
    const section = structure.sections[i];
    if (!section.sequences) continue;

    let isSet = false;
    for (let j = 0; j < section.sequences.length; j++) {
      const sequence = section.sequences[j];
      if (!sequence.verticals) continue;
      for (let k = 0; k < sequence.verticals.length; k++) {
        arr.push({
          route: [i, j, k].join("/"),
          title: section["section_title"],
        });
        isSet = true;
        break;
      }

      if (isSet) break;
    }
  }
  return arr;
};

/**
 * Returns true when the vertical is a quiz or final-evaluation mock exam.
 *
 * @param {Array} mapper Module grade-format mapper.
 * @param {Object} structure Course outline.
 * @param {number} sectionIndex Section index.
 * @param {number} sequenceIndex Sequence index.
 * @param {number} verticalIndex Vertical index.
 * @returns {boolean}
 */
export const isVerticalQuiz = (mapper, structure, sectionIndex, sequenceIndex, verticalIndex) => {
  if (!mapper || !mapper.length || !structure || !structure.sections || structure.sections.length <= sectionIndex) return false;

  const section = structure.sections[sectionIndex];

  // If module type is not quiz or evaluation finale then return false
  const mapperItem = mapper.find((m) => m.sectionTitle === section.section_title);

  if (!mapperItem || (mapperItem["gradeFormat"] && mapperItem["gradeFormat"].toLowerCase().trim() !== "quiz" && mapperItem["gradeFormat"].toLowerCase().trim() !== "évaluation finale")) return false;

  if (!section.sequences || section.sequences.length <= sequenceIndex) return false;

  const sequence = section.sequences[sequenceIndex];

  if (!sequence.verticals || sequence.verticals.length < verticalIndex) return false;

  const vertical = sequence.verticals[verticalIndex];
  if (!vertical || !vertical.xblock_ids) return false;

  for (let xid of vertical.xblock_ids) {
    if (xid.type === "mockexam") {
      return true;
    }
  }
  return false;
};

/**
 * Collects the first mock-exam vertical route for each quiz section.
 *
 * @param {Object} structure Course outline.
 * @returns {Array<{ route: string, title: string }>}
 */
export const getQuizModuleLinks = (structure) => {
  // Set Next/Prev link
  let arr = [];
  for (let i = 0; i < structure.sections.length; i++) {
    const section = structure.sections[i];
    if (!section.sequences) continue;

    let isSet = false;
    for (let j = 0; j < section.sequences.length; j++) {
      const sequence = section.sequences[j];
      if (!sequence.verticals) continue;
      for (let k = 0; k < sequence.verticals.length; k++) {
        const vertical = sequence.verticals[k];
        if (!vertical.xblock_ids) continue;
        for (let k1 = 0; k1 < vertical.xblock_ids.length; k1++) {
          const xblock_id = vertical.xblock_ids[k1];
          if (xblock_id.type === "mockexam") {
            arr.push({
              route: [i, j, k].join("/"),
              title: section["section_title"],
            });
            isSet = true;
            break;
          }
        }
        if (isSet) break;
      }
      if (isSet) break;
    }
  }
  return arr;
};

/**
 * Parses problem XML into question/answer option arrays.
 *
 * @param {Array<{ html: string, id: string }>} problems Problem payloads.
 * @returns {Array<{ id: string, question: string, answers: Array<{ option: string, text: string }> }>}
 */
export const getQAArray = (problems) => {
  // Extract problem question and answers array
  const qaArray = [];
  const parser = new DOMParser();
  problems.forEach(({ html, id }) => {
    const xml = parser.parseFromString(html, "text/xml");
    const items = xml.querySelector("problem") ? xml.querySelector("problem").children : [];

    for (let tag of items) {
      const tagName = tag.tagName ? tag.tagName.toLowerCase() : null;
      if (!tagName) continue;

      qaArray.push({
        id: id,
        question: tag.querySelector("label").innerHTML,
        answers: [],
      });

      const choices = tag.querySelector("choicegroup").children;
      for (let j = 0; j < choices.length; j++) {
        qaArray[qaArray.length - 1].answers.push({
          option: "choice_" + j,
          text: choices[j].innerHTML,
        });
      }
    }
  });
  return qaArray;
};
